import * as THREE from 'three';

/**
 * Material & Texture Inspection Suite.
 * Generates custom shader and material inspection modes for PBR maps,
 * combined textured normals (normal map + geometry), tangents, UVs, and unpacked ORM channels.
 */

// Custom Shader: Pure Geometry Normals mapped to 0..1 range
const GeoNormalsShaderMaterial = new THREE.ShaderMaterial({
  vertexShader: `
    varying vec3 vNormal;
    void main() {
      vNormal = normalize(normalMatrix * normal);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    varying vec3 vNormal;
    void main() {
      vec3 n = normalize(vNormal) * 0.5 + vec3(0.5);
      gl_FragColor = vec4(n, 1.0);
    }
  `,
  side: THREE.DoubleSide
});

// Custom Shader: Tangents mapped to 0..1 range
const TangentsShaderMaterial = new THREE.ShaderMaterial({
  vertexShader: `
    attribute vec4 tangent;
    varying vec3 vTangent;
    void main() {
      vTangent = normalize(normalMatrix * tangent.xyz) * 0.5 + vec3(0.5);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    varying vec3 vTangent;
    void main() {
      gl_FragColor = vec4(vTangent, 1.0);
    }
  `,
  side: THREE.DoubleSide
});

// Custom Shader: UV Coordinates layout RG gradient
const UVShaderMaterial = new THREE.ShaderMaterial({
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    varying vec2 vUv;
    void main() {
      vec2 grid = fract(vUv * 10.0);
      float line = step(0.95, grid.x) + step(0.95, grid.y);
      vec3 col = mix(vec3(vUv.x, vUv.y, 0.0), vec3(1.0), min(line, 1.0));
      gl_FragColor = vec4(col, 1.0);
    }
  `,
  side: THREE.DoubleSide
});

// Custom Material: Overdraw Heatmap calibrated for 0..10 layers (10 layers = Pure White 1.0,1.0,1.0)
const OverdrawMaterial = new THREE.MeshBasicMaterial({
  color: new THREE.Color(0x66b2ff),
  transparent: true,
  opacity: 0.25,
  blending: THREE.AdditiveBlending,
  depthWrite: false,
  depthTest: true,
  side: THREE.DoubleSide
});

/**
 * Creates custom channel unpacker shader material for Metallic (B), Roughness (G), or AO (R).
 */
function createChannelUnpackerMaterial(texture, channelKey = 'g', fallbackVal = 0.5) {
  if (!texture) {
    return new THREE.ShaderMaterial({
      fragmentShader: `void main() { gl_FragColor = vec4(vec3(${fallbackVal.toFixed(2)}), 1.0); }`,
      vertexShader: `void main() { gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`
    });
  }

  return new THREE.ShaderMaterial({
    uniforms: {
      uMap: { value: texture }
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D uMap;
      varying vec2 vUv;
      void main() {
        vec4 tex = texture2D(uMap, vUv);
        float val = tex.${channelKey};
        gl_FragColor = vec4(vec3(val), 1.0);
      }
    `,
    side: THREE.DoubleSide
  });
}

/**
 * Scans a loaded model hierarchy to detect all active texture maps present.
 */
export function scanAvailableTextureModes(lodMap) {
  const detected = new Set();

  lodMap.forEach((scene) => {
    scene.traverse((node) => {
      if (node.isMesh && node.material) {
        const mats = Array.isArray(node.material) ? node.material : [node.material];
        mats.forEach((m) => {
          if (m.map) detected.add('baseColor');
          if (m.normalMap) detected.add('normalMap');
          if (m.roughnessMap) detected.add('roughnessMap');
          if (m.metalnessMap) detected.add('metalnessMap');
          if (m.aoMap) detected.add('aoMap');
          if (m.emissiveMap) detected.add('emissiveMap');
          if (m.bumpMap) detected.add('bumpMap');
          if (m.displacementMap) detected.add('displacementMap');
          if (m.alphaMap) detected.add('alphaMap');
        });
      }
    });
  });

  const modes = [
    { key: 'pbr', label: 'Default PBR Render' },
    { key: 'flat', label: 'Flat Shaded (Untextured Geometry)' },
    { key: 'clay', label: 'Clay Render (Smooth Matcap)' },
    { key: 'overdraw', label: '[Optimization] Overdraw Heatmap' },
    { key: 'normals', label: 'Combined Textured Normals (0..1 Space)' },
    { key: 'geoNormals', label: 'Geometry Normals (0..1 Space)' },
    { key: 'tangents', label: 'Tangents (0..1 Space)' },
    { key: 'uv', label: 'UV Coordinates Layout' }
  ];

  if (detected.has('baseColor')) modes.push({ key: 'baseColor', label: '[Texture] Base Color / Albedo Map' });
  if (detected.has('normalMap')) modes.push({ key: 'normalMap', label: '[Texture] Normal Map Texture' });
  if (detected.has('roughnessMap')) modes.push({ key: 'roughness', label: '[Texture] Unpacked Roughness Map (Green Channel)' });
  if (detected.has('metalnessMap')) modes.push({ key: 'metallic', label: '[Texture] Unpacked Metallic Map (Blue Channel)' });
  if (detected.has('aoMap')) modes.push({ key: 'ao', label: '[Texture] Unpacked Ambient Occlusion Map (Red Channel)' });
  if (detected.has('emissiveMap')) modes.push({ key: 'emissive', label: '[Texture] Emissive Map' });
  if (detected.has('bumpMap')) modes.push({ key: 'bump', label: '[Texture] Bump Map' });
  if (detected.has('displacementMap')) modes.push({ key: 'displacement', label: '[Texture] Displacement Map' });

  return modes;
}

/**
 * Returns the appropriate inspection material based on modeKey.
 */
export function getInspectorMaterial(originalMaterial, modeKey) {
  if (!originalMaterial) {
    return new THREE.MeshStandardMaterial({ color: 0xcccccc });
  }

  const orig = Array.isArray(originalMaterial) ? originalMaterial[0] : originalMaterial;

  switch (modeKey) {
    case 'flat':
      // Pure untextured flat-shaded geometry
      return new THREE.MeshStandardMaterial({
        color: 0x9ca3af,
        roughness: 0.7,
        metalness: 0.1,
        flatShading: true
      });

    case 'clay':
      // Smooth untextured clay matcap render
      return new THREE.MeshStandardMaterial({
        color: 0xd1d5db,
        roughness: 0.85,
        metalness: 0.05,
        flatShading: false
      });

    case 'overdraw':
      // Overdraw Heatmap: Additive blending with translucency and disabled depth write
      return OverdrawMaterial;

    case 'normals':
      // Combined Textured Normals: Evaluates normal map texture + geometry in 0..1 RGB space
      if (orig.normalMap) {
        return new THREE.MeshNormalMaterial({
          normalMap: orig.normalMap,
          normalScale: orig.normalScale || new THREE.Vector2(1, 1),
          flatShading: false,
          side: THREE.DoubleSide
        });
      }
      return GeoNormalsShaderMaterial;

    case 'geoNormals':
      // Pure Geometry Normals in 0..1 RGB space
      return GeoNormalsShaderMaterial;

    case 'tangents':
      // 0..1 Tangents Space
      return TangentsShaderMaterial;

    case 'uv':
      // UV Coordinates Layout
      return UVShaderMaterial;

    case 'baseColor':
      // Unlit Base Color (Albedo map or material color)
      return new THREE.MeshBasicMaterial({
        map: orig.map || null,
        color: orig.color ? orig.color.clone() : new THREE.Color(0xffffff),
        side: THREE.DoubleSide
      });

    case 'normalMap':
      // Raw Normal Map Texture display
      return new THREE.MeshBasicMaterial({
        map: orig.normalMap || null,
        side: THREE.DoubleSide
      });

    case 'metallic':
      // Unpack Metallic Channel (GLTF Metalness Map is in Blue channel)
      {
        const map = orig.metalnessMap || orig.roughnessMap || orig.map;
        const val = orig.metalness !== undefined ? orig.metalness : 0.0;
        return createChannelUnpackerMaterial(map, 'b', val);
      }

    case 'roughness':
      // Unpack Roughness Channel (GLTF Roughness Map is in Green channel)
      {
        const map = orig.roughnessMap || orig.metalnessMap || orig.map;
        const val = orig.roughness !== undefined ? orig.roughness : 0.5;
        return createChannelUnpackerMaterial(map, 'g', val);
      }

    case 'ao':
      // Unpack Ambient Occlusion Channel (GLTF AO Map is in Red channel)
      {
        const map = orig.aoMap || orig.map;
        return createChannelUnpackerMaterial(map, 'r', 1.0);
      }

    case 'emissive':
      return new THREE.MeshBasicMaterial({
        map: orig.emissiveMap || null,
        color: orig.emissive ? orig.emissive.clone() : new THREE.Color(0xffffff),
        side: THREE.DoubleSide
      });

    case 'bump':
      return new THREE.MeshBasicMaterial({
        map: orig.bumpMap || null,
        side: THREE.DoubleSide
      });

    case 'displacement':
      return new THREE.MeshBasicMaterial({
        map: orig.displacementMap || null,
        side: THREE.DoubleSide
      });

    case 'pbr':
    default:
      // Full original PBR material with textures
      {
        if (Array.isArray(originalMaterial)) {
          return originalMaterial.map(m => {
            const c = m.clone();
            c.flatShading = false;
            c.needsUpdate = true;
            return c;
          });
        }
        const c = originalMaterial.clone();
        c.flatShading = false;
        c.needsUpdate = true;
        return c;
      }
  }
}
